import { useEffect, useState } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { AlertModal } from '../../components/AlertModal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { PromptModal } from '../../components/PromptModal';

const API = import.meta.env.VITE_API_URL;

export default function AffiliateApplications() {
  const [applications, setApplications] = useState<any[]>([]);
  const [status, setStatus] = useState('submitted');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/affiliates/applications?status=${status}`, { credentials: 'include' });
      const json = (await res.json()) as { success: boolean; data: any[]; error?: string };
      if (json.success) setApplications(json.data || []);
      else setError(json.error || 'Failed to load applications');
    } catch (e) {
      setError(`Could not reach the server: ${String(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const executeApprove = async () => {
    const id = approveId;
    setApproveId(null);
    if (!id) return;
    setError(null);
    try {
      const res = await fetch(`${API}/admin/affiliates/applications/${id}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const json = (await res.json()) as { success: boolean; data?: any; error?: string };
      if (!json.success) {
        setError(json.error || 'Approval failed');
        return;
      }
      setSuccess(`Affiliate created (id: ${json.data?.affiliate_id}). Go to the Affiliates list to complete setup.`);
      setSelected(null);
      fetchApps();
    } catch (e) {
      setError(`Could not reach the server: ${String(e)}`);
    }
  };

  const executeReject = async (v: Record<string, string>) => {
    const id = rejectId;
    setRejectId(null);
    if (!id) return;
    try {
      const res = await fetch(`${API}/admin/affiliates/applications/${id}/reject`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: v.reason }),
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) {
        setError(json.error || 'Rejection failed');
        return;
      }
      setSelected(null);
      fetchApps();
    } catch (e) {
      setError(`Could not reach the server: ${String(e)}`);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Applications</h2>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-3 py-1.5 text-sm">
          <option value="submitted">Submitted (pending review)</option>
          <option value="under_review">Under Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
      </div>


      <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : applications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No {status === 'all' ? '' : status} applications. The public application form is part of Phase 3.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3">Submitted</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {applications.map((a) => (
                <tr key={a.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(a)}>
                  <td className="p-3 text-xs">{new Date(a.submitted_at).toLocaleDateString()}</td>
                  <td className="p-3">{a.submitter_email}</td>
                  <td className="p-3">{a.submitted_data?.display_name || a.submitted_data?.legal_name || '—'}</td>
                  <td className="p-3 text-xs">{a.status}</td>
                  <td className="p-3 text-right">
                    {a.status === 'submitted' && (
                      <div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setApproveId(a.id)} className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700">
                          <CheckCircle className="w-3 h-3" /> Approve
                        </button>
                        <button onClick={() => setRejectId(a.id)} className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200">
                          <XCircle className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-medium">Application Detail</h3>
            <button onClick={() => setSelected(null)} className="text-gray-500 text-sm">Close</button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-gray-500">ID:</div><div className="font-mono">{selected.id}</div>
            <div className="text-gray-500">Submitted:</div><div>{new Date(selected.submitted_at).toLocaleString()}</div>
            <div className="text-gray-500">Email:</div><div>{selected.submitter_email}</div>
            <div className="text-gray-500">Status:</div><div>{selected.status}</div>
            {selected.reviewed_at && <><div className="text-gray-500">Reviewed:</div><div>{new Date(selected.reviewed_at).toLocaleString()}</div></>}
            {selected.admin_notes && <><div className="text-gray-500">Admin notes:</div><div>{selected.admin_notes}</div></>}
            {selected.ip_address && <><div className="text-gray-500">IP:</div><div className="font-mono text-xs">{selected.ip_address}</div></>}
          </div>
          <div className="mt-4">
            <div className="text-xs font-medium text-gray-700 mb-1">Submitted Data</div>
            <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">{JSON.stringify(selected.submitted_data, null, 2)}</pre>
          </div>
        </div>
      )}

      <AlertModal isOpen={!!error} title="Error" message={error || ''} onClose={() => setError(null)} />
      <AlertModal isOpen={!!success} title="Success" variant="success" message={success || ''} onClose={() => setSuccess(null)} />
      <ConfirmModal
        isOpen={!!approveId}
        title="Approve Application"
        message="An affiliate record will be created in onboarding status. You can complete the affiliate's PII via the Edit page afterward."
        confirmLabel="Approve"
        onConfirm={executeApprove}
        onCancel={() => setApproveId(null)}
      />
      <PromptModal
        isOpen={!!rejectId}
        title="Reject Application"
        message="The reason below will be included in the email sent to the applicant."
        danger
        submitLabel="Reject"
        fields={[{ name: 'reason', label: 'Reason for rejection', type: 'textarea', required: true }]}
        onSubmit={executeReject}
        onCancel={() => setRejectId(null)}
      />
    </div>
  );
}
